import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { hiveAuthTypes } from 'app/model/linkedservice/hive.linkedservice';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { enableSslWithTUAARadioSwitch, enableSslSimplified } from './linked-service-shared-fields';
import { format, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { supportAzureBlobType } from './azure-storage-shared-fields';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { IDataflowBuildInTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { ConnectionAuthKind } from 'shell-models-lib';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { GlobalVariables } from 'app/services/globals';
import { driverVersionVersionOptions, driverVersions } from '../consts/tabular-consts';

const usernameControl: ControlMetadata = {
    type: 'textbox',
    bind: 'username',
    label: stringResources.ModelName_UserName,
    required: true,
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    isLabelVertical: true
};

const passwordControl: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    bind: 'password',
    label: stringResources.ModelName_Password,
    child: {
        type: 'password',
        label: stringResources.ModelName_Password,
        required: true,
        isLabelVertical: true
    },
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    description: format(
        stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Hive_DisplayText),
        stringResources.ModelName_Password
    )
};
const serviceDiscoveryModeControl: ControlMetadata = {
    type: 'radio',
    bind: 'serviceDiscoveryMode',
    label: stringResources.ModelName_ServiceDiscoveryMode,
    isLabelVertical: true,
    options: [
        {
            label: stringResources.Enable,
            value: true
        },
        {
            label: stringResources.Disable,
            value: false
        }
    ],
    defaultValue: false
};
const zooKeeperNameSpaceControl: ControlMetadata = {
    type: 'textbox',
    bind: 'zooKeeperNameSpace',
    label: stringResources.ModelName_ZooKeeperNameSpace,
    isLabelVertical: true
};
const useNativeQueryControl: ControlMetadata = {
    type: 'radio',
    bind: 'useNativeQuery',
    label: stringResources.ModelName_UseNativeQuery,
    isLabelVertical: true,
    options: [
        {
            label: stringResources.Enable,
            value: true
        },
        {
            label: stringResources.Disable,
            value: false
        }
    ],
    defaultValue: false
};

function httpPathControl(authType: string, version: string): ControlMetadata {
    return {
        type: 'textbox',
        bind: 'httpPath',
        label: stringResources.ModelName_HttpPath,
        isLabelVertical: true,
        defaultValue:
            authType === hiveAuthTypes.WindowsAzureHDInsightService && version === driverVersions.v2
                ? '/hive2'
                : undefined
    };
}
const dataflowHive: IDataflowBuildInTypeMetadata = {
    type: DatasetFormatType.Hive,
    settings: adfModelTypeMap.hive.dataflowSettings,
    useInlineProjection: true,
    enableImportSchemaInSink: true
};
function authTypeControl(thriftProtocolType: string, defaultAuthType: string, version: string): ControlMetadata {
    return {
        type: 'select-switch',
        bind: 'authenticationType',
        label: stringResources.ModelName_AuthenticationType,
        defaultValue: defaultAuthType,
        options: [
            {
                // SASL doesnot support Anonymous authentication
                label: stringResources.Anonymous_DisplayText,
                value: hiveAuthTypes.Anonymous,
                hidden: thriftProtocolType === 'SASL'
            },
            {
                // Binary doesnot support username and password
                label: stringResources.AuthenticationType_UsernameAndPassword,
                value: hiveAuthTypes.UsernameAndPassword,
                hidden: thriftProtocolType === 'Binary'
            },
            {
                // Binary and SASL doesnot support Windows Azure HDInsight Service authentication
                label: stringResources.AuthenticationType_WindowsAzureHDInsightService,
                value: hiveAuthTypes.WindowsAzureHDInsightService,
                hidden: thriftProtocolType !== 'HTTP'
            }
        ],
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true,
        cases: getAuthTypeCases(version)
    };
}
function getAuthTypeCases(version: string): ControlMetadata['cases'] {
    return [
        {
            caseValue: hiveAuthTypes.Anonymous,
            children: version === driverVersions.v2 ? [httpPathControl(hiveAuthTypes.Anonymous, version)] : []
        },
        {
            caseValue: hiveAuthTypes.Username,
            children: version === driverVersions.v2 ? [] : [usernameControl]
        },
        {
            caseValue: hiveAuthTypes.UsernameAndPassword,
            children:
                version === driverVersions.v2
                    ? [usernameControl, passwordControl, httpPathControl(hiveAuthTypes.UsernameAndPassword, version)]
                    : [usernameControl, passwordControl]
        },
        {
            caseValue: hiveAuthTypes.WindowsAzureHDInsightService,
            children:
                version === driverVersions.v2
                    ? [
                          usernameControl,
                          passwordControl,
                          httpPathControl(hiveAuthTypes.WindowsAzureHDInsightService, version)
                      ]
                    : [usernameControl, passwordControl]
        }
    ];
}
function connectionForm(version: string): ControlMetadata[] {
    return [
        {
            type: 'textbox',
            bind: 'host',
            label: stringResources.ModelName_Host,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            warningMessageCalculator: (value: string) =>
                stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning,
            isLabelVertical: true
        },
        {
            type: 'textbox',
            bind: 'port',
            label: stringResources.ModelName_Port,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true,
            inputType: 'number',
            defaultValue: 443,
            min: 0
        },
        {
            type: 'select',
            bind: 'serverType',
            label: stringResources.ModelName_ServerType,
            isLabelVertical: true,
            defaultValue: version === driverVersions.v2 ? 'HiveServer2' : undefined,
            options:
                version === driverVersions.v2
                    ? [
                          {
                              label: stringResources.HiveServer2_DisplayText,
                              value: 'HiveServer2'
                          }
                      ]
                    : [
                          {
                              label: stringResources.HiveServer1_DisplayText,
                              value: 'HiveServer1'
                          },
                          {
                              label: stringResources.HiveServer2_DisplayText,
                              value: 'HiveServer2'
                          },
                          {
                              label: stringResources.HiveThriftServer_DisplayText,
                              value: 'HiveThriftServer'
                          }
                      ]
        },
        {
            type: 'select-switch',
            bind: 'thriftTransportProtocol',
            label: stringResources.ModelName_ThriftTransportProtocol,
            isLabelVertical: true,
            defaultValue: version === driverVersions.v2 ? 'HTTP' : undefined,
            options: [
                {
                    label: stringResources.ModelName_BinaryDataset,
                    value: 'Binary'
                },
                {
                    label: stringResources.SASL_displayName,
                    value: 'SASL'
                },
                {
                    label: stringResources.HttpServer_DisplayText,
                    value: 'HTTP'
                }
            ],
            cases: [
                {
                    caseValue: 'Binary',
                    children:
                        version === driverVersions.v2
                            ? [authTypeControl('Binary', hiveAuthTypes.Anonymous, driverVersions.v2)]
                            : []
                },
                {
                    caseValue: 'SASL',
                    children:
                        version === driverVersions.v2
                            ? [authTypeControl('SASL', hiveAuthTypes.UsernameAndPassword, driverVersions.v2)]
                            : []
                },
                {
                    caseValue: 'HTTP',
                    children:
                        version === driverVersions.v2
                            ? [authTypeControl('HTTP', hiveAuthTypes.Anonymous, driverVersions.v2)]
                            : []
                }
            ]
        },
        {
            type: 'select-switch',
            bind: 'authenticationType',
            label: stringResources.ModelName_AuthenticationType,
            // auth type depends on thriftTransportProtocol in V2
            hide: version === driverVersions.v2,
            options: [
                {
                    label: stringResources.Anonymous_DisplayText,
                    value: hiveAuthTypes.Anonymous
                },
                {
                    label: stringResources.UsernameLabel,
                    value: hiveAuthTypes.Username,
                    hidden: version === driverVersions.v2
                },
                {
                    label: stringResources.AuthenticationType_UsernameAndPassword,
                    value: hiveAuthTypes.UsernameAndPassword
                },
                {
                    label: stringResources.AuthenticationType_WindowsAzureHDInsightService,
                    value: hiveAuthTypes.WindowsAzureHDInsightService
                }
            ],
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true,
            defaultValue: hiveAuthTypes.Anonymous,
            cases: getAuthTypeCases(version)
        },
        {
            type: 'linkedservice-select-for-sidenav',
            bind: 'azureStorageLinkedService',
            linkedServiceTypes: [...supportAzureBlobType, adfModelTypeMap.azureBlobFS.linkedServiceType],
            label: stringResources.AzureStorage_LinkedServiceLabel,
            description: stringResources.Hive_AzureStorageLinkedServiceDescription,
            newLinkedServiceOptionText: stringResources.AddNew
        }
    ];
}
export const hiveMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.hive.linkedServiceType,
    displayName: stringResources.Hive_DisplayText,
    supportedInTrident: GlobalVariables.tridentBatch1ConnectorPhase2,
    supportOPDG: GlobalVariables.opdgPhase2,
    isDeprecated: GlobalVariables.showLegacy ? undefined : '{ version !== "v2" }',
    supportAutoFillCopilotInput: GlobalVariables.tridentBatch1ConnectorPhase2,
    isKnownInPipelineContext: GlobalVariables.tridentBatch1ConnectorPhase2,
    supportCopyJobSource: GlobalVariables.tridentBatch1ConnectorPhase2,
    supportedJobModesAsSource: GlobalVariables.copyJobIncrementalCopyPhase2 ? undefined : ['Batch'],
    pqoDataSourceOption: {
        kind: DataSourceOptionKind.Extension,
        resourceKind: 'ApacheHive' as ResourceKind
    },
    supportedAuthKindsInTrident: [ConnectionAuthKind.Basic],
    linkedServiceKind: 'table',
    icon: {
        className: 'svg-i-100x100-Hive'
    },
    supportDataFlowSource: true,
    supportDataFlowSink: true,
    dataflowSource: [dataflowHive],
    dataflowSink: [dataflowHive],
    showWarningMessage: '{ version !== "v2" }',
    warningMessage: format(
        stringResources.Connector_Legacy_IsNotSupportedWarning_alt5,
        stringResources.VersionLabel.toLocaleLowerCase()
    ),
    warningMessageHyperlink: 'https://learn.microsoft.com/azure/data-factory/connector-hive',
    isFirstClass: true,
    category: ['Database'],
    supportDatasetV2: true,
    copySinkTypes: [],
    copySourceTypes: [adfModelTypeMap.hive.copySourceType],
    SHIRVersion:  {
        v2: '5.54.0.0'
    }, 
    controls:
        [
            {
                type: 'radio-switch',
                bind: 'version',
                label: stringResources.VersionLabel,
                isLabelVertical: true,
                required: true,
                hide: GlobalVariables.showLegacy ? false : '{version === "v2" }',
                options: driverVersionVersionOptions,
                defaultValue: driverVersions.v2,
                cases: [
                    {
                        caseValue: driverVersions.v1,
                        children: [
                        {
                            type: 'hint',
                            hintMode: 'warning',
                            description: format(
                                stringResources.Connector_Legacy_IsNotSupportedWarning_alt6,
                                stringResources.VersionLabel.toLocaleLowerCase()),
                            link: 'https://learn.microsoft.com/azure/data-factory/connector-hive'
                        },
                        ...connectionForm(driverVersions.v1),
                        serviceDiscoveryModeControl,
                        zooKeeperNameSpaceControl,
                        useNativeQueryControl,
                        httpPathControl(undefined, driverVersions.v1),
                        enableSslWithTUAARadioSwitch
                        ]
                    },
                    {
                        caseValue: driverVersions.v2,
                        children: [...connectionForm(driverVersions.v2), enableSslSimplified]
                    }
                ]
            },
            ...([
                ...connectionForm(driverVersions.v2).map((control) => ({
                    ...control,
                    hide: combineHide(control.hide)
                })),
                {
                    ...enableSslSimplified,
                    hide: !GlobalVariables.showLegacy ? '{version !== "v2"}' : true

                }
            ])
        ]
};

function combineHide(origHide: boolean | string | (() => boolean) | undefined): boolean | string {
    const extraHide = !GlobalVariables.showLegacy ? '{version !== "v2"}' : true;

    if (origHide === undefined) {
        return extraHide;
    }

    return (origHide as boolean | string) || extraHide;
}
